/** Browser measurement eligibility is separate from product authorization. */
export type AnalyticsAudience =
  | "pending"
  | "eligible_unclassified"
  | "excluded_internal_or_qa"
  | "unresolved";

// QA automation may seed this boolean before navigation. Never store identities.
export const ANALYTICS_QA_EXCLUSION_KEY = "fairpath_analytics_qa_excluded";
export const ANALYTICS_CONSENT_EVENT = "fairpath:cookie-consent-changed";
type AudienceState = { audience: AnalyticsAudience; generation: number; consentRevision: number; listeners: Set<() => void> };
const states = new WeakMap<Window, AudienceState>();
const observedStorageDenials = new WeakSet<StorageEvent>();

function stateForWindow(): AudienceState {
  let state = states.get(window);
  if (!state) {
    state = { audience: "pending", generation: 0, consentRevision: 0, listeners: new Set() };
    states.set(window, state);
  }
  try {
    if (window.sessionStorage.getItem(ANALYTICS_QA_EXCLUSION_KEY) === "1") {
      state.audience = "excluded_internal_or_qa";
    }
  } catch {
    if (state.audience !== "excluded_internal_or_qa") state.audience = "unresolved";
  }
  return state;
}

function publish(state: AudienceState, audience: AnalyticsAudience): void {
  if (state.audience === audience) return;
  state.audience = audience;
  for (const listener of state.listeners) {
    try { listener(); } catch { /* Measurement must never interrupt the app. */ }
  }
}

export function getAnalyticsAudience(): AnalyticsAudience {
  return typeof window === "undefined" ? "unresolved" : stateForWindow().audience;
}

/** Synchronous auth callback work only; the RPC runs separately. */
export function beginAnalyticsAudienceResolution(): number {
  if (typeof window === "undefined") return -1;
  const state = stateForWindow();
  state.generation += 1;
  if (state.audience !== "excluded_internal_or_qa") publish(state, "pending");
  return state.generation;
}

export function resolveAnalyticsAudience(generation: number, isFounder: boolean | null): void {
  if (typeof window === "undefined") return;
  const state = stateForWindow();
  if (state.generation !== generation || state.audience === "excluded_internal_or_qa") return;
  if (isFounder === true) { excludeAnalyticsForSession(); return; }
  // Storage denial cannot establish that this session did not retain QA state.
  try { window.sessionStorage.getItem(ANALYTICS_QA_EXCLUSION_KEY); }
  catch { publish(state, "unresolved"); return; }
  publish(state, isFounder === false ? "eligible_unclassified" : "unresolved");
}

export function excludeAnalyticsForSession(): void {
  if (typeof window === "undefined") return;
  const state = stateForWindow();
  try { window.sessionStorage.setItem(ANALYTICS_QA_EXCLUSION_KEY, "1"); }
  catch { /* The in-memory latch still protects this document. */ }
  publish(state, "excluded_internal_or_qa");
}

export function hasAcceptedAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem("fairpath_cookie_consent") === "accepted"; }
  catch { return false; }
}

/** A queued cross-tab denial remains meaningful after storage advances again. */
export function getAnalyticsConsentRevision(): number {
  return typeof window === "undefined" ? 0 : stateForWindow().consentRevision;
}

/** Also notifies consumers after an in-document consent change. */
export function subscribeAnalyticsPermission(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const target = window;
  const state = stateForWindow();
  state.listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== "fairpath_cookie_consent" && event.key !== null) return;
    if (event.newValue !== "accepted" && !observedStorageDenials.has(event)) {
      observedStorageDenials.add(event);
      state.consentRevision += 1;
    }
    listener();
  };
  target.addEventListener(ANALYTICS_CONSENT_EVENT, listener);
  target.addEventListener("storage", onStorage);
  return () => {
    state.listeners.delete(listener);
    target.removeEventListener(ANALYTICS_CONSENT_EVENT, listener);
    target.removeEventListener("storage", onStorage);
  };
}
